import { Suspense, useEffect, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { PromoBanner } from './components/common/PromoBanner';
import { useGeoCurrencyOverride } from './hooks/useGeoCurrencyOverride';
import { trackPageView } from './utils/analytics';
import { hideCrisp, loadCrisp } from './utils/crisp';

// Route-level code splitting to improve initial load and Core Web Vitals.
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const GenerationPage = lazy(() => import('./pages/GenerationPage').then((m) => ({ default: m.GenerationPage })));
const GalleryPage = lazy(() => import('./pages/GalleryPage').then((m) => ({ default: m.GalleryPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const MyCreationsPage = lazy(() => import('./pages/MyCreationsPage').then((m) => ({ default: m.MyCreationsPage })));
const MyOrdersPage = lazy(() => import('./pages/MyOrdersPage').then((m) => ({ default: m.MyOrdersPage })));
const CreationPage = lazy(() => import('./pages/CreationPage').then((m) => ({ default: m.CreationPage })));
const ShippingPage = lazy(() => import('./pages/ShippingPage').then((m) => ({ default: m.ShippingPage })));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage').then((m) => ({ default: m.OrderSuccessPage })));
const ReviewOrderPage = lazy(() => import('./pages/ReviewOrderPage').then((m) => ({ default: m.ReviewOrderPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const SpecialCreationPage = lazy(() => import('./pages/SpecialCreationPage').then((m) => ({ default: m.SpecialCreationPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage').then((m) => ({ default: m.AboutUsPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then((m) => ({ default: m.ContactPage })));
const ReturnRefundPage = lazy(() => import('./pages/ReturnRefundPage').then((m) => ({ default: m.ReturnRefundPage })));

export default function App() {
  // Trigger geo detection on app boot, but do not block rendering.
  // (Blocking here caused the landing page to flash the generation loading UI.)
  const { blocked } = useGeoCurrencyOverride();

  const location = useLocation();
  // TEMP DEBUG: validate what React Router thinks the current route is.
  // eslint-disable-next-line no-console
  console.log('[RR render]', location.pathname);
  const isFullBleed = location.pathname === '/gallery';

  // Ensure route changes start at the top of the page.
  // React Router doesn't do scroll restoration by default.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  // TEMP DEBUG: confirm React Router location updates when navbar links are clicked.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[RR location]', location.pathname);
  }, [location.pathname]);

  // GA4 SPA pageview tracking: we configure GA with `send_page_view: false`,
  // so we must send page_view events manually on route changes.
  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // Crisp chat: show only on marketing pages.
  useEffect(() => {
    const marketingPaths = new Set<string>(['/', '/pricing', '/about', '/contact']);
    const shouldShow = marketingPaths.has(location.pathname);

    if (!shouldShow) {
      hideCrisp();
      return;
    }

    const websiteId = import.meta.env.VITE_CRISP_WEBSITE_ID as string | undefined;
    if (!websiteId) {
      // No Crisp configured for this environment.
      return;
    }

    loadCrisp(websiteId);
  }, [location.pathname]);

  const marketingPaths = new Set<string>(['/', '/pricing', '/about', '/contact']);

  // Promo banner expiry: stop showing after Jan 31, 2026 (local time).
  // Using local time is typically what you want for marketing promos.
  const PROMO_END_MS = new Date(2026, 1, 1, 0, 0, 0, 0).getTime(); // Feb 1, 2026 00:00:00
  const promoActive = Date.now() < PROMO_END_MS;

  const showPromoBanner = marketingPaths.has(location.pathname) && promoActive;

  return (
    <div className="min-h-screen flex flex-col font-sans text-palette-4">
      {showPromoBanner && (
        <PromoBanner
          message={
            <span>
              🎉 Storeylines is celebrating the new year with a 10% discount across all products. Use{' '}
              <span className="font-extrabold">NEWYEAR26</span> at checkout! Valid until January 31.
            </span>
          }
        />
      )}
      <Navbar />
      {blocked && (
        <div className="bg-red-50 border-b border-red-200 text-red-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-sm">
            This store currently serves customers in the United States and Canada only.
          </div>
        </div>
      )}
      <main className={`flex-grow py-2 md:py-2 pb-24 md:pb-0 ${isFullBleed ? '' : 'container-page'}`}>
        <Suspense
          fallback={
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-gray-600">
              Loading…
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/create" element={<GenerationPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/my-creations" element={<MyCreationsPage />} />
            <Route path="/my-orders" element={<MyOrdersPage />} />
            <Route path="/creation/:id" element={<CreationPage />} />
            <Route path="/shipping/:creationId" element={<ShippingPage />} />
            <Route path="/order-success/:creationId" element={<OrderSuccessPage />} />
            <Route path="/review-order/:creationId" element={<ReviewOrderPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/special/:token" element={<SpecialCreationPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/returns" element={<ReturnRefundPage />} />
          </Routes>
        </Suspense>
      </main>
      <footer className="bg-white border-t border-palette-2/20 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-palette-4/60 text-sm">
          <p className="font-medium">Storeylines is owned and operated by Bruno Joyal.</p>
          <div className="mt-2 space-x-4">
            <a href="/contact" className="underline underline-offset-4 hover:text-palette-4">
              Contact
            </a>
            <a href="/returns" className="underline underline-offset-4 hover:text-palette-4">
              Returns & Refunds
            </a>
            <a href="/privacy" className="underline underline-offset-4 hover:text-palette-4">
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
